import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { fileUrl } from "@/lib/api";

const PlayerContext = createContext(null);
const RATES = [1, 1.25, 1.5, 0.75];

export function PlayerProvider({ children }) {
  const audioRef = useRef(null);
  const pendingSeek = useRef(null);
  const rateRef = useRef(1);
  const [track, setTrack] = useState(null); // { artwork, lang, slug }
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [rate, setRate] = useState(1);

  useEffect(() => {
    const a = new Audio();
    a.preload = "auto";
    audioRef.current = a;
    const applySeek = () => {
      if (pendingSeek.current == null || !Number.isFinite(a.duration) || !a.duration) return;
      a.currentTime = Math.max(0, Math.min(pendingSeek.current(a.duration), a.duration));
      pendingSeek.current = null;
    };
    a.addEventListener("timeupdate", () => setTime(a.currentTime));
    a.addEventListener("durationchange", () => setDuration(Number.isFinite(a.duration) ? a.duration : 0));
    a.addEventListener("loadedmetadata", applySeek);
    a.addEventListener("canplay", applySeek);
    a.addEventListener("play", () => setPlaying(true));
    a.addEventListener("pause", () => setPlaying(false));
    a.addEventListener("ended", () => setPlaying(false));
    return () => { a.pause(); a.src = ""; };
  }, []);

  const audioUrlFor = (artwork, lang) => fileUrl(artwork?.content?.[lang]?.audio_url);

  const safePlay = (a) => {
    const p = a.play();
    if (p && p.catch) p.catch(() => {
      const once = () => { a.removeEventListener("canplay", once); a.play().catch(() => {}); };
      a.addEventListener("canplay", once);
    });
  };

  const load = useCallback((artwork, lang, slug, { autoplay = false, seekFn = null } = {}) => {
    const a = audioRef.current;
    const url = audioUrlFor(artwork, lang);
    setTrack({ artwork, lang, slug });
    setTime(0); setDuration(0);
    if (!url) { a.pause(); a.removeAttribute("src"); a.load(); return; }
    pendingSeek.current = seekFn;
    a.src = url;
    a.playbackRate = rateRef.current;
    a.load();
    if (autoplay) safePlay(a);
  }, []);

  const toggle = useCallback(() => {
    const a = audioRef.current;
    if (!a.getAttribute("src")) return;
    if (a.paused) { if (a.readyState === 0) a.load(); safePlay(a); } else a.pause();
  }, []);

  const seek = useCallback((t) => { const a = audioRef.current; a.currentTime = Math.max(0, Math.min(t, a.duration || t)); setTime(a.currentTime); }, []);
  const skip = useCallback((d) => { const a = audioRef.current; seek(a.currentTime + d); }, [seek]);
  const cycleRate = useCallback(() => {
    const next = RATES[(RATES.indexOf(rateRef.current) + 1) % RATES.length];
    rateRef.current = next; setRate(next); audioRef.current.playbackRate = next;
  }, []);

  const switchLanguage = useCallback((lang) => {
    if (!track) return;
    const a = audioRef.current;
    const ratio = a.duration && Number.isFinite(a.duration) ? a.currentTime / a.duration : 0;
    const wasPlaying = !a.paused && !!a.getAttribute("src");
    load(track.artwork, lang, track.slug, { autoplay: wasPlaying, seekFn: (newDur) => ratio * newDur - 3 });
  }, [track, load]);

  const resumeTimeFor = () => Math.max(0, time - 3);

  return (
    <PlayerContext.Provider value={{ track, playing, time, duration, rate, load, toggle, seek, skip, cycleRate, switchLanguage, resumeTimeFor, hasAudio: !!(track && audioUrlFor(track.artwork, track.lang)) }}>
      {children}
    </PlayerContext.Provider>
  );
}

export const usePlayer = () => useContext(PlayerContext);
